import { useEffect, useRef, useCallback } from 'react';

export function useLocalStorage<T>(
  key: string,
  value: T,
  enabled: boolean,
  delayMs = 300,
  storageKind: 'local' | 'session' = 'local'
): void {
  const storageKindRef = useRef(storageKind);
  storageKindRef.current = storageKind;

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The most recent (key, value) that has not been written yet.
  const pendingRef = useRef<{ key: string; value: unknown } | null>(null);

  const writeNow = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const pending = pendingRef.current;
    if (!pending) return;
    pendingRef.current = null;
    try {
      const store = storageKindRef.current === 'session' ? sessionStorage : localStorage;
      store.setItem(pending.key, JSON.stringify(pending.value));
    } catch {
      // Storage unavailable
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    pendingRef.current = { key, value };
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(writeNow, delayMs);
  }, [key, value, enabled, delayMs, writeNow]);

  // The debounce window must not swallow the final write: flush it when the
  // hook unmounts and when the page is being hidden/unloaded.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.addEventListener('pagehide', writeNow);
    return () => {
      window.removeEventListener('pagehide', writeNow);
      writeNow();
    };
  }, [writeNow]);
}

export function readLocalStorage<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}
